import type { QueryResult } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import type {
	perIssueMetricsRelayQuery$data,
	perIssueMetricsRelayQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/perIssueMetricsRelayQuery.graphql';
import {
	type CloudId,
	type ProjectId,
	toIssueId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import type { DateRange, IssueMetrics, TopFilter } from '../../common/types';
import { getResourceId } from '../../common/utils/ari';
import { toServiceShapeTopFilter } from '../../common/utils/issue-filters';

export const buildQueryVariables = (
	cloudId: CloudId,
	projectId: ProjectId | null,
	dateRange: DateRange | null,
	first: number,
	filters: TopFilter,
): perIssueMetricsRelayQuery$variables => ({
	first,
	filter: {
		cloudId,
		// @ts-expect-error - TS2322 - Relay type can't be null
		startFromInclusive: dateRange ? dateRange.startDateInclusive.toISOString() : null,
		// @ts-expect-error - TS2322 - Relay type can't be null
		endAtExclusive: dateRange ? dateRange.endDateExclusive.toISOString() : null,
		jiraProjectIds: projectId ? [projectId] : [],
		issueFilters: toServiceShapeTopFilter(cloudId, filters),
	},
});

const getValidatedData = (
	queryResult: QueryResult<perIssueMetricsRelayQuery$variables, perIssueMetricsRelayQuery$data>,
): IssueMetrics[] => {
	const perIssueMetrics = queryResult.data?.devOpsMetrics?.perIssueMetrics;
	const nodes = perIssueMetrics?.nodes;
	const { error } = queryResult;

	// This is when something is unexpectedly wrong
	if (perIssueMetrics == null || nodes == null) {
		throw Error(
			`Cycle time report: perIssueMetrics is missing ${
				queryResult.error ? `, ${queryResult.error.message}` : ''
			}`,
		);
	}

	// This is when the data doesn't exist. nodes will be an empty array.
	if (nodes.length === 0) {
		return [];
	}

	return nodes.map((x) => {
		// Not sure why this could happen, it shouldn't happen, but Flow is happy with this.
		if (x == null) {
			throw new Error(
				`Cycle time report: null/undefined node found while iterating perIssueMetrics nodes.${
					error ? ` GraphQL error msg: ${error.message}` : ''
				}`,
			);
		}

		const {
			id,
			totalCycleTimeSeconds,
			meanReviewTimeSeconds,
			commitsCount,
			pullRequestsCount,
			lastSuccessfulProductionDeployment,
		} = x;

		// Not sure why this could happen, it shouldn't happen, but Flow is happy with this.
		// This shouldn't happen unless we change our query not to return one of these
		if (
			totalCycleTimeSeconds == null ||
			commitsCount == null ||
			pullRequestsCount == null ||
			lastSuccessfulProductionDeployment == null
		) {
			throw new Error(
				`Cycle time report: null/undefined node property found while iterating perIssueMetrics nodes.${
					error ? ` GraphQL error msg: ${error.message}` : ''
				}`,
			);
		}

		return {
			id: toIssueId(getResourceId(id)),
			totalCycleTimeSeconds,
			meanReviewTimeSeconds: meanReviewTimeSeconds ?? null,
			commitsCount,
			pullRequestsCount,
			lastSuccessfulProductionDeployment: new Date(lastSuccessfulProductionDeployment),
		};
	});
};

export const transformResult = (
	queryResult: QueryResult<perIssueMetricsRelayQuery$variables, perIssueMetricsRelayQuery$data>,
): ServiceQueryResultWithFetch<IssueMetrics[]> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
		fetch: queryResult.refetch,
	};

	if (queryResult.loading) {
		return resultBase;
	}

	try {
		return {
			...resultBase,
			data: getValidatedData(queryResult),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error,
		};
	}
};
